import * as PIXI from 'pixi.js'
import { TOP_PANEL_TITLE_STYLE, TOP_PANEL_VALUE_STYLE } from '@/containers/common/TextStyles'
import GameSignals from '@/signals/GameSignals'
import { Utilities } from '@base/utils/Utilities'
import Facade from '@/Facade'
import gsap from 'gsap'
import { IInitResponse, Orientation } from '@/modules'
import GeneralSignals from '@/signals/GeneralSignals'
import { toMoney } from '@base/utils/Formatter'
import { TranslationUtils } from '@modules/src/utils/TranslationUtils'
import { DeviceUtils } from '@modules/src/utils/DeviceUtils'
import { SpecialSymbolType } from '@/containers/symbols/SpecialSymbolType'

export class WinPanelContainer extends PIXI.Container {
    private readonly _back: PIXI.Sprite
    private readonly _winText: PIXI.Text
    private readonly _multiplierValue: PIXI.Text

    public static readonly DURATION: number = 0.5
    public static readonly EASING: string = 'power2.out'

    private _lastTween: gsap.core.Tween
    private _counteredWin: number
    private _totalWin: number
    private _currencyCode: string
    private _fsTopSpinTumble: number
    private _fsTopSpinMultiplier: number
    private _fsTopPendingCollectedMultiplier: number
    private _fsTopTotal: number
    private _fsTopBasePartial: number
    private _fsTopSequenceTweenA: gsap.core.Tween
    private _fsTopSequenceTweenB: gsap.core.Tween

    public constructor() {
        super()

        GameSignals.init.connect(this.onInit)
        GameSignals.updateWinPanelValue.connect(this.onUpdateWinPanelValue)
        GeneralSignals.orientationChanged.connect(this.onUpdateLayout)
        GameSignals.startSpin.connect(this.onstartSpin)
        GameSignals.multiplierState.connect(this.onMultiplierState)
        GameSignals.completeMultiplierState.connect(this.onCompleteMultiplierState)
        GameSignals.showBaseGameBackground.connect(this.onShowBaseGameBackground)

        this._lastTween = null
        this.visible = false
        this._fsTopSpinTumble = 0
        this._fsTopSpinMultiplier = 1
        this._fsTopPendingCollectedMultiplier = 1
        this._fsTopTotal = 0
        this._fsTopBasePartial = 0
        this._fsTopSequenceTweenA = null
        this._fsTopSequenceTweenB = null

        this._back = new PIXI.Sprite(PIXI.Texture.from('images/winPanel'))
        this._back.anchor.set(0.5)
        this.addChild(this._back)

        this._winText = new PIXI.Text(TranslationUtils.translate('winPanelTitle'), TOP_PANEL_TITLE_STYLE)
        this._winText.anchor.set(0.5)
        this._winText.position.set(0, -32)
        this.addChild(this._winText)

        this._multiplierValue = new PIXI.Text('', TOP_PANEL_VALUE_STYLE)
        this._multiplierValue.anchor.set(0.5)
        this._multiplierValue.position.set(0, 6)
        this.addChild(this._multiplierValue)

        this.onUpdateLayout(Facade.orientation)
    }

    private onInit = (data: IInitResponse) => {
        const currency = String(data.player?.currency || '').toUpperCase()
        const currencyByCode: Record<string, string> = { EUR: '€', USD: '$', GBP: '£', TRY: '₺', RUB: '₽' }
        this._currencyCode = currencyByCode[currency] || currency || ''

        this._reset()
    }

    private onUpdateWinPanelValue = (value: number, duration: number = -1, force: boolean = false): void => {
        if (this._totalWin === 0) {
            this.alpha = 0
            this.visible = true
            gsap.to(this, {
                duration: 0.5,
                alpha: 1,
            })
        }

        if (value < 0 || (value === 0 && !force)) {
            return
        }

        if (Facade.model?.roundState?.isFreeSpins) {
            this._fsTopBasePartial += value
            this._fsTopSpinTumble = this._fsTopBasePartial

            GameSignals.fsTopValuesFlow.emit({
                tumble: this._fsTopSpinTumble,
                multiplier: this._fsTopSpinMultiplier,
                total: this._fsTopTotal,
                animateTumble: true,
                duration: duration === -1 ? WinPanelContainer.DURATION : duration,
            })
        }

        const counter = { value: this._counteredWin }
        const localAccumulatedTotal = this._totalWin + value
        const expectedCascadeTotal = this.getExpectedCascadeTotal()
        this._totalWin = Math.max(localAccumulatedTotal, expectedCascadeTotal)

        if (duration === -1) {
            duration = WinPanelContainer.DURATION
        }

        if (this._lastTween) {
            this._lastTween.kill()
            this._lastTween = null
        }

        this._lastTween = gsap.to(counter, {
            duration,
            ease: WinPanelContainer.EASING,
            value: this._totalWin,
            onUpdate: () => {
                this._multiplierValue.text = this._currencyCode + toMoney(counter.value, 2)
                this._counteredWin = counter.value
            },
            onComplete: () => {
                this._lastTween = null
            },
        })
    }

    private getExpectedCascadeTotal(): number {
        const combinationsByCascade = Facade.model?.spinData?.winning_combinations
        const currentCascadeIndex = Number(Facade.model?.cascadeIndex)

        if (!Array.isArray(combinationsByCascade) || !Number.isFinite(currentCascadeIndex) || currentCascadeIndex < 0) {
            return 0
        }

        let total = 0
        const lastIndex = Math.min(currentCascadeIndex, combinationsByCascade.length - 1)

        for (let i = 0; i <= lastIndex; i++) {
            const combinations = combinationsByCascade[i]
            if (!Array.isArray(combinations)) {
                continue
            }

            combinations.forEach((combination: any) => {
                const win = Number(combination?.win_currency)
                if (Number.isFinite(win) && win > 0) {
                    total += win / 100
                }
            })
        }

        return total
    }

    private _reset = () => {
        this._counteredWin = 0
        this._totalWin = 0
        this._multiplierValue.text = ''
    }

    private onstartSpin = (): void => {
        if (this.visible) {
            gsap.to(this, {
                duration: 0.5,
                alpha: 0,
                onComplete: () => {
                    this.visible = false
                    this.alpha = 1
                },
            })
        }
        this._reset()

        this.clearFsTopSequence()
        this._fsTopSpinTumble = 0
        this._fsTopSpinMultiplier = 1
        this._fsTopPendingCollectedMultiplier = 1
        this._fsTopBasePartial = 0
        this._fsTopTotal = this.getServerFsTotalWinAmount()

        if (Facade.model?.roundState?.isFreeSpins) {
            GameSignals.fsTopValuesFlow.emit({
                tumble: 0,
                multiplier: 1,
                total: this._fsTopTotal,
            })
        }
    }

    private onCompleteMultiplierState = (): void => {
        if (!Facade.model?.roundState?.isFreeSpins) {
            return
        }

        this.clearFsTopSequence()

        this._fsTopSpinMultiplier = Math.max(
            1,
            this._fsTopPendingCollectedMultiplier || this.getCurrentSpinMultiplierFromGrid()
        )

        GameSignals.fsTopValuesFlow.emit({
            tumble: this._fsTopSpinTumble,
            multiplier: this._fsTopSpinMultiplier,
            total: this._fsTopTotal,
            animateMultiplier: true,
        })

        const shouldApplyMultiplier = this._fsTopSpinMultiplier > 1 && this._fsTopBasePartial > 0

        this._fsTopSequenceTweenA = gsap.delayedCall(1, () => {
            if (shouldApplyMultiplier) {
                this._fsTopSpinTumble = this._fsTopBasePartial * this._fsTopSpinMultiplier
                GameSignals.fsTopValuesFlow.emit({
                    tumble: this._fsTopSpinTumble,
                    multiplier: this._fsTopSpinMultiplier,
                    total: this._fsTopTotal,
                    animateTumble: true,
                })
            }

            this._fsTopSequenceTweenB = gsap.delayedCall(1, () => {
                const serverTotal = this.getServerFsTotalWinAmount()
                this._fsTopTotal = serverTotal > 0 ? serverTotal : Math.max(this._fsTopTotal, this._fsTopSpinTumble)

                GameSignals.fsTopValuesFlow.emit({
                    tumble: this._fsTopSpinTumble,
                    multiplier: this._fsTopSpinMultiplier,
                    total: this._fsTopTotal,
                    animateTotal: true,
                })
            })
        })
    }

    private onShowBaseGameBackground = (): void => {
        this.clearFsTopSequence()
        this._fsTopSpinTumble = 0
        this._fsTopSpinMultiplier = 1
        this._fsTopPendingCollectedMultiplier = 1
        this._fsTopBasePartial = 0
        this._fsTopTotal = 0

        GameSignals.fsTopValuesFlow.emit({
            tumble: 0,
            multiplier: 1,
            total: 0,
        })
    }

    private clearFsTopSequence(): void {
        if (this._fsTopSequenceTweenA) {
            this._fsTopSequenceTweenA.kill()
            this._fsTopSequenceTweenA = null
        }

        if (this._fsTopSequenceTweenB) {
            this._fsTopSequenceTweenB.kill()
            this._fsTopSequenceTweenB = null
        }
    }

    private onMultiplierState = (lastGridData: any[][]): void => {
        if (!Facade.model?.roundState?.isFreeSpins || !Array.isArray(lastGridData)) {
            return
        }

        let multiplier = 0

        lastGridData.forEach((reel: any[]) => {
            if (!Array.isArray(reel)) {
                return
            }

            reel.forEach((symbol: any) => {
                if (symbol?.id === SpecialSymbolType.MULTIPLIER) {
                    const value = Number(symbol?.multiplier)
                    if (Number.isFinite(value)) {
                        multiplier += value
                    }
                }
            })
        })

        this._fsTopPendingCollectedMultiplier = Math.max(1, multiplier)
    }

    private getCurrentSpinMultiplierFromGrid(): number {
        const grid = Facade.model?.spinData?.slot_grid?.[Facade.model?.cascadeIndex]
        if (!Array.isArray(grid)) {
            return 1
        }

        let multiplier = 0
        grid.forEach((reel: any[]) => {
            if (!Array.isArray(reel)) {
                return
            }

            reel.forEach((symbol: any) => {
                if (symbol?.id === SpecialSymbolType.MULTIPLIER) {
                    const value = Number(symbol?.multiplier)
                    if (Number.isFinite(value)) {
                        multiplier += value
                    }
                }
            })
        })

        return Math.max(1, multiplier)
    }

    private getServerFsTotalWinAmount(): number {
        const freebet = Facade.model?.spinData?.freebet
        if (!freebet) {
            return 0
        }

        const totalWin = Number(freebet.total_win)
        if (Number.isFinite(totalWin)) {
            return Math.max(0, totalWin)
        }

        const totalCoinWin = Number(freebet.total_coin_win)
        if (Number.isFinite(totalCoinWin)) {
            return Math.max(0, totalCoinWin)
        }

        return 0
    }

    private onUpdateLayout = (orientation: string) => {
        if (orientation === Orientation.LANDSCAPE) {
            this.scale.set(1)
            this.position.set(0, DeviceUtils.isMobileDevice() ? -480 : -446)
            return
        }

        this.scale.set(1.3)
        this.position.set(170, -576)
    }
}
